class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.skuSearchCache = {}; // Cache for SKU search results
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.pendingSingleProductRedirect = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      
      // Clear SKU cache for unrelated searches
      const newSearchUpper = newSearchTerm.trim().toUpperCase();
      const keysToRemove = Object.keys(this.skuSearchCache).filter(key => {
        return !newSearchUpper.startsWith(key) && !key.startsWith(newSearchUpper);
      });
      keysToRemove.forEach(key => delete this.skuSearchCache[key]);
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.pendingSingleProductRedirect = false;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  getProductHandleFromUrl(href) {
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]).toLowerCase() : null;
    } catch (error) {
      return null;
    }
  }

  getSingleProductUrl() {
    const productItems = this.querySelectorAll(
      '#predictive-search-results-products-list .predictive-search__list-item, #predictive-search-results-sku-products-list .predictive-search__list-item'
    );
    const uniqueProducts = new Map();

    productItems.forEach((item) => {
      const href = item.querySelector('a[href]')?.getAttribute('href');
      const handle = this.getProductHandleFromUrl(href);
      if (!handle) return;

      const existingHref = uniqueProducts.get(handle);
      const prefersVariantUrl = href.includes('variant=') && (!existingHref || !existingHref.includes('variant='));

      if (!existingHref || prefersVariantUrl) {
        uniqueProducts.set(handle, href);
      }
    });

    if (uniqueProducts.size !== 1) return null;

    return uniqueProducts.values().next().value;
  }

  getProductHandlesFromMarkup(markup) {
    const handles = new Set();
    const parsedMarkup = new DOMParser().parseFromString(markup, 'text/html');

    parsedMarkup.querySelectorAll('#predictive-search-results-products-list a[href]').forEach((link) => {
      const handle = this.getProductHandleFromUrl(link.getAttribute('href'));
      if (handle) handles.add(handle);
    });

    return handles;
  }

  getUniqueSkuResults(skuResults, resultsMarkup) {
    if (!skuResults || skuResults.length === 0) return [];

    const existingHandles = this.getProductHandlesFromMarkup(resultsMarkup);
    const uniqueSkuResults = [];
    const seenHandles = new Set();

    skuResults.forEach((item) => {
      const handle = item.product?.handle?.toLowerCase();
      if (!handle || existingHandles.has(handle) || seenHandles.has(handle)) return;

      seenHandles.add(handle);
      uniqueSkuResults.push(item);
    });

    return uniqueSkuResults;
  }

  hideDuplicateProductItems() {
    const seenHandles = new Set();
    const productItems = this.querySelectorAll(
      '#predictive-search-results-products-list .predictive-search__list-item, #predictive-search-results-sku-products-list .predictive-search__list-item'
    );

    productItems.forEach((item) => {
      const handle = this.getProductHandleFromUrl(item.querySelector('a[href]')?.getAttribute('href'));
      if (!handle) return;

      if (seenHandles.has(handle)) {
        item.remove();
        return;
      }

      seenHandles.add(handle);
    });

    const skuList = this.querySelector('#predictive-search-results-sku-products-list');
    if (skuList && !skuList.querySelector('.predictive-search__list-item')) {
      skuList.closest('.predictive-search__result-group')?.remove();
    }
  }

  goToSingleProductIfAvailable() {
    const productUrl = this.getSingleProductUrl();
    if (!productUrl) return false;

    window.location.assign(productUrl);
    return true;
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    if (this.querySelector('[aria-selected="true"]')) return;

    if (this.goToSingleProductIfAvailable()) {
      event.preventDefault();
      return;
    }

    if (this.hasAttribute('loading')) {
      event.preventDefault();
      this.pendingSingleProductRedirect = true;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.pendingSingleProductRedirect = false;
      // Clear SKU cache on form reset
      this.skuSearchCache = {};
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Check if search term looks like SKU (alphanumeric, no spaces, 2+ chars)
    const looksLikeSKU = /^[A-Za-z0-9\-_]{2,20}$/.test(searchTerm.trim()) && !searchTerm.includes(' ');

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        // Check if no products found and query looks like SKU
        const hasProducts = resultsMarkup.includes('predictive-search-option-product');
        
        // Always search by SKU if query looks like SKU (supports both exact and partial matches)
        if (looksLikeSKU) {
          // Check cache first
          const skuCacheKey = searchTerm.trim().toUpperCase();
          
          if (this.skuSearchCache[skuCacheKey]) {
            const cachedSkuResults = this.getUniqueSkuResults(this.skuSearchCache[skuCacheKey], resultsMarkup);
            if (cachedSkuResults.length > 0) {
              const skuResultsHTML = this.generateSKUResultsHTML(cachedSkuResults);
              return resultsMarkup + skuResultsHTML;
            }
            return resultsMarkup;
          }
          
          // Search by SKU via JavaScript API
          // OPTIMIZED LOGIC for SKU format: [Letter][5 digits] (e.g., R12506, G13462) or [6 digits] (e.g., 111345)
          // - For 6 character queries that match SKU pattern: prioritize exact match (faster)
          // - For shorter queries (3-5 chars): use partial match to support progressive typing
          // - For longer queries (7+ chars): use exact match only when no standard results
          const searchTermLen = searchTerm.trim().length;
          
          // Check if query matches typical SKU format: [Letter][5 digits] or [6 digits]
          const isTypicalSKUFormat = searchTermLen === 6 && /^[A-Z]\d{5}$|^\d{6}$/.test(skuCacheKey);
          
          // Exact match strategy:
          // - Full SKU (6 chars matching pattern): exact match for speed
          // - Very long queries (8+ chars) without standard results: exact match
          // - Everything else: partial match to support progressive typing
          const exactMatchOnly = isTypicalSKUFormat || (searchTermLen >= 8 && !hasProducts);
          
          return this.searchBySKU(searchTerm.trim().toUpperCase(), exactMatchOnly).then((skuResults) => {
            // Cache the results
            this.skuSearchCache[skuCacheKey] = skuResults;
            
            const uniqueSkuResults = this.getUniqueSkuResults(skuResults, resultsMarkup);
            if (uniqueSkuResults.length > 0) {
              const skuResultsHTML = this.generateSKUResultsHTML(uniqueSkuResults);
              const combinedMarkup = resultsMarkup + skuResultsHTML;
              return combinedMarkup;
            }
            return resultsMarkup;
          }).catch(() => {
            return resultsMarkup;
          });
        }
        
        return resultsMarkup;
      })
      .then((finalMarkup) => {
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = finalMarkup;
        });
        this.renderSearchResults(finalMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  async searchBySKU(sku, exactMatch = false) {
    const foundProducts = [];
    const skuUpper = sku.toUpperCase();
    const maxResults = exactMatch ? 1 : 10;
    
    // Increased pages for better product coverage
    // For exact matches, search through more pages to ensure we find the product
    const maxPages = exactMatch ? 16 : 12; // 16 pages = 4000 products for exact, 12 = 3000 for partial
    
    try {
      // Use parallel requests for faster search when checking multiple pages
      const pagePromises = [];
      const pagesToCheck = [];
      
      // Build array of pages to check
      for (let page = 1; page <= maxPages; page++) {
        pagesToCheck.push(page);
      }
      
      // Fetch pages in parallel (but limit to 4 concurrent requests to avoid overwhelming)
      const concurrentLimit = 4;
      for (let i = 0; i < pagesToCheck.length; i += concurrentLimit) {
        const pageBatch = pagesToCheck.slice(i, i + concurrentLimit);
        
        const batchPromises = pageBatch.map(page => 
          fetch(`/collections/all/products.json?limit=250&page=${page}`, {
            signal: this.abortController.signal,
          })
          .then(response => {
            if (!response.ok) return null;
            return response.json();
          })
          .catch(() => null)
        );
        
        const batchResults = await Promise.all(batchPromises);
        
        // Process results from this batch
        for (let j = 0; j < batchResults.length; j++) {
          if (foundProducts.length >= maxResults) break;
          
          const data = batchResults[j];
          if (!data || !data.products || data.products.length === 0) continue;
          
          // Process products from this page
          for (const product of data.products) {
            if (foundProducts.length >= maxResults) break;
            
            // Quick check: skip if product has no variants
            if (!product.variants || product.variants.length === 0) continue;
            
            for (const variant of product.variants) {
              if (variant.sku) {
                const variantSkuUpper = variant.sku.toUpperCase();
                
                // Optimize matching logic
                let matches = false;
                if (exactMatch) {
                  matches = variantSkuUpper === skuUpper;
                  if (matches) {
                    // For exact match, return immediately
                    foundProducts.push({
                      product: product,
                      variant: variant
                    });
                    return foundProducts; // Early return for exact match
                  }
                } else {
                  // Fast path for exact match
                  if (variantSkuUpper === skuUpper) {
                    matches = true;
                  } else if (skuUpper.length >= 3) {
                    // Use startsWith first (faster than includes for prefix matches)
                    if (variantSkuUpper.startsWith(skuUpper)) {
                      matches = true;
                    }
                    // Only check includes if startsWith failed (for queries like "123" in "R1234")
                    else if (variantSkuUpper.includes(skuUpper)) {
                      matches = true;
                    }
                  }
                }
                
                if (matches) {
                  foundProducts.push({
                    product: product,
                    variant: variant
                  });
                  break; // Found match, move to next product
                }
              }
            }
          }
        }
        
        // Early exit if we found exact match
        if (exactMatch && foundProducts.length > 0) {
          break;
        }
        
        // Stop if we have enough results
        if (foundProducts.length >= maxResults) {
          break;
        }
      }
    } catch (error) {
      if (error?.code !== 20) { // Ignore aborted errors
        console.warn('Error searching for SKU:', error);
      }
    }
    
    return foundProducts;
  }

  generateSKUResultsHTML(skuResults) {
    if (!skuResults || skuResults.length === 0) return '';
    
    // Get current shop currency/money format from window.Shopify if available
    const formatMoney = (cents) => {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '{{amount}}');
      }
      // Fallback: simple format without spaces
      const amount = (cents / 100).toFixed(2);
      const currency = window.Shopify?.currency?.active || 'EUR';
      return `${amount} ${currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency}`;
    };
    
    // Helper to get image URL from various formats
    const getImageUrl = (product) => {
      // Try featured_image first
      if (product.featured_image) {
        return typeof product.featured_image === 'string' 
          ? product.featured_image 
          : product.featured_image.src || '';
      }
      // Try variant featured_image
      if (product.variants && product.variants.length > 0) {
        for (const v of product.variants) {
          if (v.featured_image) {
            const img = v.featured_image;
            if (typeof img === 'string') return img;
            if (img.src) return img.src;
          }
        }
      }
      // Try images array (can be strings or objects)
      if (product.images && product.images.length > 0) {
        for (const img of product.images) {
          if (typeof img === 'string') return img;
          if (img && img.src) return img.src;
        }
      }
      // Try image (legacy format)
      if (product.image) {
        if (typeof product.image === 'string') return product.image;
        if (product.image.src) return product.image.src;
      }
      return '';
    };
    
    let html = '<div class="predictive-search__result-group"><div>';
    html += '<h2 id="predictive-search-sku-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>';
    html += '<ul id="predictive-search-results-sku-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-sku-products">';
    
    skuResults.forEach((item, index) => {
      const product = item.product;
      const variant = item.variant;
      const imageUrl = getImageUrl(product);
      const priceFormatted = variant.price ? formatMoney(variant.price) : '';
      
      html += `<li id="predictive-search-option-sku-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
      html += `<a href="/products/${product.handle}?variant=${variant.id}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">`;
      
      if (imageUrl) {
        html += `<img class="predictive-search__image" src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(product.title)}" width="50" height="50" loading="lazy">`;
      }
      
      html += `<div class="predictive-search__item-content${!imageUrl && !priceFormatted ? ' predictive-search__item-content--centered' : ''}">`;
      html += `<p class="predictive-search__item-heading h5">${this.escapeHtml(product.title)}</p>`;
      html += `<p class="predictive-search__item-sku text-body-small" style="color: #666; font-size: 12px; margin-top: 2px;">${this.escapeHtml(variant.sku)}</p>`;
      
      // Don't show price - match standard predictive search style
      
      html += `</div></a></li>`;
    });
    
    html += '</ul></div></div>';
    return html;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.hideDuplicateProductItems();

    this.setLiveRegionResults();
    this.open();

    if (this.pendingSingleProductRedirect) {
      this.pendingSingleProductRedirect = false;
      if (!this.goToSingleProductIfAvailable()) {
        this.input.form.submit();
      }
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
